// The order page, redesigned (owner, 2026-10-07 — sketch in
// docs/order-page-redesign/, answers 1-9 in the chat).
//
//   Step bar   Photos · Album · PDF · Preview · Delivery · Follow-up. Tap one:
//              it opens in place (the others stay as they are — answer 7).
//   Pinned     one red block, only while a note is an OPEN PROBLEM; it says
//              what to do and has « Mark as done » (answer 2).
//   Steps      the current step first and open, the others folded to one line.
//              A note about a step sits inside that step.
//   Activity   every note, newest first, one line each, and the internal-note
//              form: plain note or problem to pin, and which step.
//   Drawer     the whole WhatsApp conversation, sliding in from the right; you
//              can answer from it (answer 6 — same sending, pauses the agent).
//
// Pieces only: AdminOrderDetailPage lays them out and keeps every card it had.
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ordersApi, type Order, type OrderNote, type OrderNoteStep } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { ChatPanel } from '../assistant/ConversationsTab';

export type StepKey = OrderNoteStep;
export interface StepInfo { key: StepKey; label: string; done: boolean }

export const STEP_LABEL: Record<StepKey, string> = {
  photos: 'Photos', album: 'Album', pdf: 'PDF', preview: 'Aperçu', delivery: 'Livraison', followup: 'Après livraison',
};

/** Open problems pin; everything else is history. Pure. */
export function openProblems(notes: OrderNote[]): OrderNote[] {
  return notes.filter((n) => n.is_problem && !n.done_at);
}

/** The order the steps are shown in: the current one first, then the rest in their order. Pure. */
export function stepOrder(steps: StepInfo[], current: StepKey): StepInfo[] {
  const cur = steps.find((s) => s.key === current);
  return cur ? [cur, ...steps.filter((s) => s.key !== current)] : steps;
}

/** Price · paid · to collect (A7). Pure. */
export function moneyOf(o: Pick<Order, 'total_price' | 'paid_in_full' | 'advance_received_amount' | 'payment_method'>) {
  const price = Math.round(Number(o.total_price ?? 0));
  const paid = o.paid_in_full ? price : Math.round(Number(o.advance_received_amount ?? 0));
  return { price, paid, toCollect: Math.max(0, price - paid), method: o.payment_method === 'virement' ? 'Virement' : 'COD' };
}

export function useOrderNotes(shopifyOrderId: string) {
  const qc = useQueryClient();
  const key = ['order-admin-notes', shopifyOrderId];
  const q = useQuery({ queryKey: key, queryFn: () => ordersApi.adminNotes(shopifyOrderId), staleTime: 15_000 });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const add = useMutation({
    mutationFn: (v: { note: string; step: StepKey | null; isProblem: boolean }) =>
      ordersApi.addAdminNote(shopifyOrderId, v.note, { step: v.step, is_problem: v.isProblem }),
    onSuccess: invalidate,
  });
  const del = useMutation({ mutationFn: (id: string) => ordersApi.deleteAdminNote(shopifyOrderId, id), onSuccess: invalidate });
  const done = useMutation({
    mutationFn: (v: { id: string; done: boolean }) => ordersApi.markAdminNoteDone(shopifyOrderId, v.id, v.done),
    onSuccess: invalidate,
  });
  return { notes: q.data?.notes ?? [], add, del, done };
}

const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', {
  timeZone: 'Africa/Casablanca', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});
const who = (email: string | null | undefined) => (email ? email.split('@')[0] : 'système');

// ─── STEP BAR ───────────────────────────────────────────────────────────────
export function StepBar({ steps, current, open, onOpen, requestsLeft = 0 }: {
  steps: StepInfo[]; current: StepKey; open: Set<StepKey>; onOpen: (k: StepKey) => void; requestsLeft?: number;
}) {
  const { t } = useT();
  return (
    <nav aria-label={t('Étapes')} className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-0.5" data-no-ptr>
      {steps.map((s, i) => {
        const isCur = s.key === current;
        return (
          <button key={s.key} type="button" onClick={() => onOpen(s.key)} data-step-tab={s.key}
            aria-current={isCur ? 'step' : undefined}
            className={`shrink-0 inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg text-[12.5px] cursor-pointer border-b-2 transition-colors ${
              open.has(s.key) ? 'border-b-text-primary' : 'border-b-transparent'
            } ${isCur ? 'bg-warning/15 text-text-primary font-semibold' : s.done ? 'text-text-secondary hover:bg-bg-elevated' : 'text-text-tertiary hover:bg-bg-elevated'}`}>
            <span aria-hidden className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
              s.done ? 'bg-success/20 text-[#56654C] dark:text-status-done' : isCur ? 'bg-warning text-white' : 'bg-bg-elevated text-text-tertiary'
            }`}>{s.done ? '✓' : i + 1}</span>
            {t(s.label)}
            {s.key === 'preview' && requestsLeft > 0 && (
              <span className="min-w-5 h-5 px-1 rounded-full bg-adm-gold text-[#2B2116] text-[11px] leading-5 text-center tabular-nums">{requestsLeft}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ─── ONE STEP, FOLDED OR OPEN ───────────────────────────────────────────────
export function StepFold({ step, index, current, open, onToggle, notes, onDeleteNote, children }: {
  step: StepInfo; index: number; current: boolean; open: boolean; onToggle: (open: boolean) => void;
  notes: OrderNote[]; onDeleteNote: (id: string) => void; children: ReactNode;
}) {
  const { t } = useT();
  return (
    <details id={`step-${step.key}`} open={open} onToggle={(e) => onToggle((e.currentTarget as HTMLDetailsElement).open)}
      className={`rounded-xl border bg-bg-card ${current ? 'border-warning/50' : 'border-border-subtle'}`}
      style={{ scrollMarginTop: 'calc(var(--adm-sticky-h, 160px) + 12px)' }}>
      <summary className="list-none cursor-pointer select-none flex items-center gap-3 px-4 py-3 min-h-[48px]">
        <span aria-hidden className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11.5px] font-semibold ${
          step.done ? 'bg-success/20 text-[#56654C] dark:text-status-done' : current ? 'bg-warning text-white' : 'bg-bg-elevated text-text-tertiary'
        }`}>{step.done ? '✓' : index}</span>
        <span className="text-[14px] font-semibold text-text-primary">{t(step.label)}</span>
        <span className={`text-[12px] ${current ? 'text-[#8F5F2A] dark:text-status-waiting font-medium' : 'text-text-tertiary'}`}>
          {current ? t('étape en cours') : step.done ? t('fait') : t('à venir')}
        </span>
        {notes.length > 0 && <span className="text-[11.5px] text-text-tertiary">· {t('{n} note(s)', { n: notes.length })}</span>}
        <span aria-hidden className="ml-auto text-text-tertiary text-[12px]">{open ? '▴' : '▾'}</span>
      </summary>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {notes.length > 0 && (
            <ul className="space-y-1.5">
              {notes.map((n) => <NoteLine key={n.id} n={n} onDelete={onDeleteNote} />)}
            </ul>
          )}
          {children}
        </div>
      )}
    </details>
  );
}

function NoteLine({ n, onDelete, onDone, showStep = false }: {
  n: OrderNote; onDelete: (id: string) => void; onDone?: (id: string, done: boolean) => void; showStep?: boolean;
}) {
  const { t } = useT();
  const open = n.is_problem && !n.done_at;
  return (
    <li className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px] ${open ? 'bg-error/10 border border-error/30' : 'bg-bg-elevated/60'}`}>
      <span aria-hidden>{open ? '📌' : n.is_problem ? '✓' : '📝'}</span>
      <div className="min-w-0 flex-1">
        <p className={`whitespace-pre-wrap break-words ${open ? 'text-error' : 'text-text-primary'}`}>{n.note}</p>
        <p className="text-[11px] text-text-tertiary mt-0.5">
          {who(n.author_email)} · {when(n.created_at)}
          {showStep && n.step ? ` · ${t(STEP_LABEL[n.step])}` : ''}
          {n.done_at ? ` · ${t('fait par {who} le {d}', { who: who(n.done_by), d: when(n.done_at) })}` : ''}
        </p>
      </div>
      {onDone && n.is_problem && (
        <button type="button" onClick={() => onDone(n.id, !n.done_at)}
          className="shrink-0 min-h-[32px] px-2.5 rounded-lg border border-border-default bg-bg-card text-[11.5px] font-semibold text-text-secondary hover:bg-bg-elevated cursor-pointer">
          {n.done_at ? t('Rouvrir') : t('Marquer comme fait')}
        </button>
      )}
      <button type="button" aria-label={t('Supprimer la note')} title={t('Supprimer la note')}
        onClick={() => { if (window.confirm(t('Supprimer cette note ?'))) onDelete(n.id); }}
        className="shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-full text-text-tertiary hover:bg-bg-card cursor-pointer">✕</button>
    </li>
  );
}

// ─── PINNED: open problems only ─────────────────────────────────────────────
export function PinnedProblems({ notes, onDone, onDelete, onGoStep }: {
  notes: OrderNote[]; onDone: (id: string, done: boolean) => void; onDelete: (id: string) => void; onGoStep: (k: StepKey) => void;
}) {
  const { t } = useT();
  const open = openProblems(notes);
  if (!open.length) return null;
  return (
    <section role="alert" aria-label={t('Problème ouvert')} className="rounded-xl border border-error/40 bg-error/10 p-3 space-y-2">
      <p className="text-[12.5px] font-semibold text-error">{t('À régler sur cette commande')}</p>
      <ul className="space-y-1.5">
        {open.map((n) => (
          <li key={n.id} className="flex items-start gap-2 text-[13px]">
            <span aria-hidden>📌</span>
            <div className="min-w-0 flex-1">
              <p className="text-error whitespace-pre-wrap break-words">{n.note}</p>
              <p className="text-[11px] text-error/80 mt-0.5">
                {who(n.author_email)} · {when(n.created_at)}
                {n.step && (<> · <button type="button" onClick={() => onGoStep(n.step!)} className="underline underline-offset-2 cursor-pointer">{t(STEP_LABEL[n.step])}</button></>)}
              </p>
            </div>
            <button type="button" onClick={() => onDone(n.id, true)}
              className="shrink-0 min-h-[36px] px-3 rounded-lg bg-text-primary text-bg-card text-[12px] font-semibold cursor-pointer">
              {t('Marquer comme fait')}
            </button>
            <button type="button" aria-label={t('Supprimer la note')} onClick={() => { if (window.confirm(t('Supprimer cette note ?'))) onDelete(n.id); }}
              className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full text-error hover:bg-error/10 cursor-pointer">✕</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── ACTIVITY: every note, and the internal-note form ───────────────────────
export function ActivityCard({ notes, add, onDelete, onDone }: {
  notes: OrderNote[];
  add: { mutate: (v: { note: string; step: StepKey | null; isProblem: boolean }, o?: { onSuccess?: () => void }) => void; isPending: boolean; error: unknown };
  onDelete: (id: string) => void; onDone: (id: string, done: boolean) => void;
}) {
  const { t } = useT();
  const [text, setText] = useState('');
  const [step, setStep] = useState<StepKey | ''>('');
  const [problem, setProblem] = useState(false);
  const [all, setAll] = useState(false);
  const shown = all ? notes : notes.slice(0, 8);
  return (
    <section className="rounded-xl border border-border-subtle bg-bg-card p-4 space-y-3">
      <h3 className="text-[14px] font-semibold text-text-primary">{t('Activité')}</h3>
      <form className="space-y-2" onSubmit={(e) => {
        e.preventDefault();
        const note = text.trim();
        if (!note) return;
        add.mutate({ note, step: step || null, isProblem: problem }, { onSuccess: () => { setText(''); setProblem(false); setStep(''); } });
      }}>
        <label htmlFor="internal-note" className="sr-only">{t('Note interne')}</label>
        <textarea id="internal-note" value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={2}
          placeholder={t('Note interne — visible par l’équipe seulement')}
          className="w-full px-3 py-2 rounded-lg border border-border-default bg-bg-card text-[13px] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500" />
        <div className="flex flex-wrap items-center gap-2">
          <select value={step} onChange={(e) => setStep(e.target.value as StepKey | '')} aria-label={t('Étape concernée')}
            className="h-9 px-2 rounded-lg border border-border-default bg-bg-card text-[12.5px] text-text-primary">
            <option value="">{t('Aucune étape')}</option>
            {(Object.keys(STEP_LABEL) as StepKey[]).map((k) => <option key={k} value={k}>{t(STEP_LABEL[k])}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-[12.5px] text-text-secondary cursor-pointer select-none min-h-[36px]">
            <input type="checkbox" checked={problem} onChange={(e) => setProblem(e.target.checked)} className="h-4 w-4 accent-[#C9A24A]" />
            {t('Problème à épingler')}
          </label>
          <button type="submit" disabled={!text.trim() || add.isPending}
            className="ml-auto h-9 px-3.5 rounded-lg bg-adm-gold text-[#2B2116] text-[12.5px] font-semibold cursor-pointer disabled:opacity-40">
            {t('Ajouter la note')}
          </button>
        </div>
        {Boolean(add.error) && <p role="alert" className="text-[12px] text-error">{(add.error as Error).message}</p>}
      </form>
      {notes.length === 0
        ? <p className="text-[12.5px] text-text-tertiary">{t('Rien pour l’instant.')}</p>
        : <ul className="space-y-1.5">{shown.map((n) => <NoteLine key={n.id} n={n} onDelete={onDelete} onDone={onDone} showStep />)}</ul>}
      {notes.length > shown.length && (
        <button type="button" onClick={() => setAll(true)} className="text-[12px] text-text-secondary underline underline-offset-2 cursor-pointer">
          {t('Tout voir ({n})', { n: notes.length })}
        </button>
      )}
    </section>
  );
}

// ─── THE CHAT DRAWER ────────────────────────────────────────────────────────
export function ChatDrawer({ chatId, name, phone, onClose }: {
  chatId: string; name: string | null; phone: string | null; onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end bg-black/30" onClick={onClose}>
      <aside role="dialog" aria-modal="true" aria-label={name || phone || chatId}
        className="h-[100dvh] w-full md:w-[520px] bg-bg-card border-l border-border-subtle shadow-2xl storiz-fade-in"
        onClick={(e) => e.stopPropagation()}>
        <ChatPanel chatId={chatId} name={name} phone={phone} onClose={onClose} />
      </aside>
    </div>,
    document.body,
  );
}

// ─── MONEY (header) ─────────────────────────────────────────────────────────
export function MoneyBlock({ order }: { order: Order }) {
  const { t } = useT();
  const m = moneyOf(order);
  if (!m.price) return null;
  const cell = (label: string, value: string, sub?: string, strong = false) => (
    <div className="min-w-0">
      <p className="text-[10.5px] text-text-tertiary leading-none">{t(label)}</p>
      <p className={`text-[13px] tabular-nums leading-tight mt-0.5 ${strong ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>{value}</p>
      {sub && <p className="text-[10px] text-text-tertiary leading-none">{t(sub)}</p>}
    </div>
  );
  return (
    <div className="flex items-start gap-4">
      {cell('Prix', `${m.price} MAD`)}
      {cell('Payé', `${m.paid} MAD`)}
      {cell('À encaisser', `${m.toCollect} MAD`, m.method, true)}
    </div>
  );
}
